/** @jsx jsx */
import { useAnalytics } from '@arfabrands/gatsby-theme-assembly'
import { Link } from 'gatsby'
import Img from 'gatsby-image'
import PropTypes from 'prop-types'
import { Fragment } from 'react'
import { Box, Flex, Grid, jsx, Text } from 'theme-ui'
import QuantitySelector from '~/components/Cart/QuantitySelector'
import useProductDetails from '~/hooks/components/use-product-details'

const LineItem = ({
  id,
  sku,
  slug,
  name,
  options,
  quantity,
  displayAmount,
  singleDisplayAmount,
  orderConfirmed = false
}) => {
  const { trackClickProduct } = useAnalytics()
  const path = sku.includes('|') ? 'bundles' : 'products'
  const url = `/${path}/${slug}/`

  const { image, parts } = useProductDetails({ sku, slug })

  return (
    <Flex
      sx={{
        flexDirection: ['column', 'row'],
        marginBottom: ['12px', '20px'],
        marginTop: ['12px', '20px'],
        justifyContent: 'space-between'
      }}
    >
      <Flex
        sx={{
          flexDirection: ['row']
        }}
      >
        {image && (
          <Box
            sx={{
              minWidth: ['96px', '144px']
            }}
          >
            <Link to={url} onClick={() => trackClickProduct(slug)}>
              <Img fluid={image.fluid} alt={image.title} />
            </Link>
          </Box>
        )}
        <Flex
          sx={{
            flexDirection: 'column',
            flexGrow: 1,
            height: '100%',
            marginLeft: '1rem'
          }}
        >
          <Text
            sx={{
              marginBottom: '0.5rem',
              variant: 'text.h4'
            }}
          >
            {name}
          </Text>
          <Text
            sx={{
              marginBottom: '0.5rem',
              variant: 'text.h4'
            }}
          >
            {options}
          </Text>

          {parts && (
            <Fragment>
              <Text
                sx={{
                  marginBottom: '0.5rem',
                  variant: 'text.h5'
                }}
              >
                Contains:
              </Text>

              <Grid columns={[1]} gap={'0.5rem'} pb={'1rem'}>
                {parts.map(part => {
                  return (
                    <Flex
                      key={part.sku}
                      sx={{
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                        alignItems: 'center'
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: ['28px'],
                          marginRight: '0.75rem'
                        }}
                      >
                        <Img
                          fluid={part.mainImage.fluid}
                          alt={part.mainImage.title}
                        />
                      </Box>
                      <Text
                        sx={{
                          variant: 'text.h5',
                          fontWeight: '400'
                        }}
                      >
                        {part.name}
                      </Text>
                    </Flex>
                  )
                })}
              </Grid>
            </Fragment>
          )}
        </Flex>
      </Flex>
      <Flex
        sx={{
          flexDirection: ['row', 'column'],
          alignItems: ['center', 'flex-end'],
          justifyContent: 'space-between',
          height: ['48px', '160px'],
          marginTop: ['12px', '0']
        }}
      >
        {orderConfirmed && <Text>{quantity}</Text>}

        {!orderConfirmed && (
          <QuantitySelector
            quantity={quantity}
            lineItemId={id}
            sx={{
              marginRight: ['0', null, '4px'],
              width: ['80px', '80px', '128px']
            }}
          />
        )}
        <Text variant="body">{`${quantity} x ${singleDisplayAmount}`}</Text>
        <Text variant="body">{displayAmount}</Text>
      </Flex>
    </Flex>
  )
}

LineItem.propTypes = {
  id: PropTypes.number.isRequired,
  sku: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  name: PropTypes.string,
  quantity: PropTypes.number,
  displayAmount: PropTypes.string,
  singleDisplayAmount: PropTypes.string,
  orderConfirmed: PropTypes.bool
}

export default LineItem
